'use client'

import * as React from 'react'
import { Info, TrendingUp } from 'lucide-react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import { EASE, SPRING } from '@/lib/motion'

// StoryCardHero, dialog-style hero that tells the story of what the
// feature does. Inspired by the Microsoft Visual Search dialog: source
// on the left, result tiles on the right, an "identified" label that
// bridges the two, a one-sentence headline, a privacy / context
// disclosure, value chips that name the customer benefit, and a CTA
// row. Each feature page composes its own <source> and <result>
// nodes; the frame, identified label, headline, value strip and CTAs
// are uniform.
//
// Use on PDP-style features that have a clean source → result flow:
// visual-search, virtual-try-on, ai-shopper, ai-tagging, collect-ugc,
// email-campaign, lead-capture, surveys, qr-campaigns, link-in-bio,
// wishlist. For features without a natural input → output (rights
// state machine, ab-test comparison, analytics volumes), use
// MechanismHero instead.
//
// Motion (June 2026): the input → output story now plays once on
// scroll-in. The source settles first, the result arrives a beat later,
// the identified verdict fades up, then the value chips pop. Reduced
// motion resolves straight to the final state.

export type StoryCardCta = { label: string; href?: string; onClick?: () => void; variant?: 'primary' | 'ghost' }

export type ValueChip = {
  /** Headline value, e.g. "+34%". */
  value: string
  /** Short label under or beside the value, e.g. "PDP conversion". */
  label: string
  /** Optional source citation, e.g. "internal benchmark, n=42 stores". */
  source?: string
}

export type StoryCardHeroProps = {
  source: React.ReactNode
  result: React.ReactNode
  /** "Identified · Linen blazer · Sand" style bridge label. Optional. */
  identified?: { eyebrow: string; value: string }
  /** One-sentence headline describing what the feature does. */
  headline: string
  /** One-line disclosure / privacy / context note. */
  disclosure?: string
  /** Bottom-right CTAs. */
  ctas?: { primary?: StoryCardCta; ghost?: StoryCardCta }
  /** Value strip, customer benefit chips. */
  valueChips?: ValueChip[]
  /** Tone of the surrounding halo. Default 'primary'. */
  tone?: 'primary' | 'forest' | 'ocean' | 'sand' | 'rose'
}

const TONES: Record<NonNullable<StoryCardHeroProps['tone']>, string> = {
  primary: 'from-primary/15 via-transparent to-emerald-200/20',
  forest:  'from-emerald-500/15 via-transparent to-teal-200/20',
  ocean:   'from-sky-500/15 via-transparent to-cyan-200/20',
  sand:    'from-amber-500/15 via-transparent to-rose-200/20',
  rose:    'from-rose-500/15 via-transparent to-amber-200/20',
}

export function StoryCardHero({
  source,
  result,
  identified,
  headline,
  disclosure,
  ctas,
  valueChips,
  tone = 'primary',
}: StoryCardHeroProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const done = inView
  const d = (t: number) => (reduce ? 0 : t)

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="relative">
        <div className={`absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br ${TONES[tone]} blur-2xl`} />

        <div className="rounded-3xl border bg-card p-5 shadow-2xl shadow-primary/10 md:p-6">
          <div className="grid gap-5 md:grid-cols-[1fr_minmax(0,180px)] md:items-start">
            {/* The input settles first… */}
            <m.div
              initial={reduce ? false : { opacity: 0, y: 14 }}
              animate={done ? { opacity: 1, y: 0 } : undefined}
              transition={{ duration: 0.5, ease: EASE }}
            >
              {source}
            </m.div>
            {/* …the output arrives a beat later. */}
            <m.div
              initial={reduce ? false : { opacity: 0, x: 12 }}
              animate={done ? { opacity: 1, x: 0 } : undefined}
              transition={{ duration: 0.5, ease: EASE, delay: d(0.4) }}
            >
              {result}
              {identified && (
                <m.div
                  className="mt-2.5"
                  initial={reduce ? false : { opacity: 0, y: 6 }}
                  animate={done ? { opacity: 1, y: 0 } : undefined}
                  transition={{ duration: 0.4, ease: EASE, delay: d(0.8) }}
                >
                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{identified.eyebrow}</p>
                  <p className="mt-0.5 text-sm font-semibold italic">{identified.value}</p>
                </m.div>
              )}
            </m.div>
          </div>

          <m.div
            className="mt-5 space-y-1"
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={done ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 0.45, ease: EASE, delay: d(0.55) }}
          >
            <h3 className="font-display text-base font-semibold tracking-tight text-balance">{headline}</h3>
            {disclosure && (
              <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                {disclosure} <Info className="h-3 w-3" />
              </p>
            )}
          </m.div>

          {valueChips && valueChips.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-2 border-t pt-4">
              {valueChips.map((c, i) => (
                <m.li
                  key={c.label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"
                  title={c.source}
                  initial={reduce ? false : { opacity: 0, scale: 0.85 }}
                  animate={done ? { opacity: 1, scale: 1 } : undefined}
                  transition={{ ...SPRING, delay: d(0.95 + i * 0.12) }}
                >
                  <TrendingUp className="h-3 w-3" />
                  <span className="tabular-nums">{c.value}</span>
                  <span className="font-medium text-emerald-700/80">{c.label}</span>
                </m.li>
              ))}
            </ul>
          )}

          {ctas && (ctas.primary || ctas.ghost) && (
            <m.div
              className="mt-4 flex items-center justify-end gap-2"
              initial={reduce ? false : { opacity: 0 }}
              animate={done ? { opacity: 1 } : undefined}
              transition={{ duration: 0.4, ease: EASE, delay: d(1.15) }}
            >
              {ctas.ghost && <StoryCta cta={ctas.ghost} variant="ghost" />}
              {ctas.primary && <StoryCta cta={ctas.primary} variant="primary" />}
            </m.div>
          )}
        </div>
      </div>
    </LazyMotion>
  )
}

function StoryCta({ cta, variant }: { cta: StoryCardCta; variant: 'primary' | 'ghost' }) {
  const cls =
    variant === 'primary'
      ? 'rounded-md bg-primary px-3.5 py-1.5 text-xs font-semibold text-primary-foreground shadow hover:brightness-95'
      : 'rounded-md border bg-background px-3 py-1.5 text-xs font-semibold text-foreground/80 hover:bg-secondary'
  if (cta.href) {
    return <a href={cta.href} className={cls}>{cta.label}</a>
  }
  return <button type="button" onClick={cta.onClick} className={cls}>{cta.label}</button>
}

// Helpers for individual source/result panels so feature pages stay
// terse. None of these are required, each page can build bespoke
// panels, but the common shapes (crop frame, result tile grid, chat
// bubble) live here so the family stays visually coherent.

export function StoryCropFrame({ src, alt = '' }: { src: string; alt?: string }) {
  // The vision moment: when the frame scrolls into view a lime scan line
  // sweeps it once (same language as the AI-tagging frame), then the crop
  // border + corner brackets draw in as "the AI found its subject".
  // Plays once; reduced motion resolves straight to the final state.
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const play = inView && !reduce
  const done = inView

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="relative aspect-square overflow-hidden rounded-2xl border bg-secondary">
        <ImgWithFallback src={src} alt={alt} fill sizes="(min-width: 1024px) 32vw, 90vw" className="object-cover" />
        <div className="absolute inset-0 bg-foreground/10" />

        {/* Scan sweep: a thin lime line + soft wash passing down the frame once. */}
        {play && (
          <m.div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 z-10 h-16"
            style={{
              background:
                'linear-gradient(to bottom, transparent, hsl(74 100% 66% / 0.22) 70%, hsl(74 100% 66% / 0.9) 99%, transparent)',
            }}
            initial={{ top: '-18%' }}
            animate={{ top: '108%' }}
            transition={{ duration: 1.1, ease: 'linear', delay: 0.2 }}
          />
        )}

        {/* Crop border + corner brackets: draw in right after the scan passes. */}
        <m.div
          className="absolute inset-[14%] rounded-md"
          initial={reduce ? false : { opacity: 0, scale: 1.04 }}
          animate={done ? { opacity: 1, scale: 1 } : undefined}
          transition={{ duration: 0.4, ease: EASE, delay: reduce ? 0 : 0.8 }}
        >
          <div className="absolute inset-0 rounded-md border border-white/85 shadow-[0_0_0_9999px_rgba(15,23,42,0.35)]" />
          <span className="absolute -left-px -top-px h-4 w-4 border-l-2 border-t-2 border-white" />
          <span className="absolute -right-px -top-px h-4 w-4 border-r-2 border-t-2 border-white" />
          <span className="absolute -bottom-px -left-px h-4 w-4 border-b-2 border-l-2 border-white" />
          <span className="absolute -bottom-px -right-px h-4 w-4 border-b-2 border-r-2 border-white" />
        </m.div>
      </div>
    </LazyMotion>
  )
}

export function StoryResultTiles({
  tiles,
}: {
  tiles: Array<{ src: string; badge?: string; alt?: string }>
}) {
  const ref = React.useRef<HTMLUListElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  return (
    <LazyMotion features={domAnimation}>
      <ul ref={ref} className="grid grid-cols-2 gap-1.5">
      {tiles.map((t, i) => (
        <m.li
          key={i}
          className="overflow-hidden rounded-md border bg-background"
          initial={reduce ? false : { opacity: 0, y: 10, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : undefined}
          transition={{ duration: 0.4, ease: EASE, delay: reduce ? 0 : 0.5 + i * 0.1 }}
        >
          <div className="relative aspect-square bg-secondary">
            <ImgWithFallback src={t.src} alt={t.alt ?? ''} fill sizes="120px" className="object-cover" />
            {t.badge && (
              <m.span
                className="absolute right-1 top-1 rounded bg-card/95 px-1 py-0.5 text-[9px] font-bold tabular-nums"
                initial={reduce ? false : { opacity: 0, scale: 0.6 }}
                animate={inView ? { opacity: 1, scale: 1 } : undefined}
                transition={{ ...SPRING, delay: reduce ? 0 : 0.75 + i * 0.1 }}
              >
                {t.badge}
              </m.span>
            )}
          </div>
        </m.li>
      ))}
      </ul>
    </LazyMotion>
  )
}
